/**
 * لایه نیم‌رخ حجم
 * ================
 * میله‌های افقی سمت چپِ نمودار: در هر **قیمت** چقدر معامله شده است.
 * روی همان بازه‌ای که الان روی صفحه دیده می‌شود حساب می‌شود (VPVR)، پس با
 * جابه‌جایی و بزرگ‌نمایی، خودش را به‌روز می‌کند.
 *
 * چرا این را اضافه کردیم: نمودارهای مبتنی بر تریدینگ‌ویو این ابزار را در
 * نسخه رایگان **ندارند**. برای معامله‌گری که دنبال محدوده‌های واقعیِ
 * دست‌به‌دست‌شدن است، همین یک ابزار از ده اندیکاتور دیگر مهم‌تر است.
 *
 * ریاضیاتش اینجا نیست — در `lib/volumeProfile.ts` است و جدا آزموده می‌شود.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { IChartApi, ISeriesApi } from 'lightweight-charts'
import type { Candle } from '../lib/indicators'
import { volumeProfile } from '../lib/volumeProfile'
import { compact } from '../lib/format'

type Props = {
  chart: IChartApi | null
  series: ISeriesApi<any> | null
  candles: Candle[]
  /** سهم عرض نمودار که میله‌ها می‌گیرند */
  widthRatio?: number
  bins?: number
}

function VolumeProfileLayerImpl({
  chart, series, candles, widthRatio = 0.22, bins = 28,
}: Props) {
  const host = useRef<HTMLDivElement>(null)
  const [tick, force] = useState(0)
  const redraw = useCallback(() => force((v) => v + 1), [])

  useEffect(() => {
    if (!chart) return
    const ts = chart.timeScale()
    ts.subscribeVisibleLogicalRangeChange(redraw)
    const ro = new ResizeObserver(redraw)
    if (host.current?.parentElement) ro.observe(host.current.parentElement)
    // محور قیمت با رویداد خبر نمی‌دهد؛ یک تپش آرام پوششش می‌دهد. وقتی
    // کندلی نیست، چیزی هم برای کشیدن نیست.
    const id = candles.length ? setInterval(redraw, 1500) : 0
    return () => {
      ts.unsubscribeVisibleLogicalRangeChange(redraw)
      ro.disconnect()
      if (id) clearInterval(id)
    }
  }, [chart, redraw, candles.length])

  const size = () => {
    const b = host.current?.getBoundingClientRect()
    const ps = (chart as any)?.paneSize?.()
    return { w: (ps?.width as number) || b?.width || 0, h: (ps?.height as number) || b?.height || 0 }
  }

  const { w, h } = size()

  const view = useMemo(() => {
    if (!chart || !series || candles.length < 2 || w < 80) return null
    // فقط کندل‌هایی که الان دیده می‌شوند
    let rows = candles
    try {
      const r = chart.timeScale().getVisibleLogicalRange()
      if (r) {
        const from = Math.max(0, Math.floor(r.from))
        const to = Math.min(candles.length, Math.ceil(r.to) + 1)
        // بزرگ‌نماییِ خیلی نزدیک: به‌جای برگشتن به «کل تاریخچه» — که یک‌باره
        // نیم‌رخِ کاملاً متفاوتی نشان می‌داد — دست‌کم چند کندل اطراف را
        // برمی‌داریم.
        const lo = Math.max(0, Math.min(from, candles.length - 3))
        const hi = Math.max(lo + 3, to)
        rows = candles.slice(lo, hi)
      }
    } catch { /* همه کندل‌ها */ }
    if (rows.length < 2) return null

    const p = volumeProfile(rows, bins)
    if (!p) return null
    const barW = Math.max(30, Math.min(w * widthRatio, w * 0.4))
    const yOf = (price: number): number | null => {
      const v = series.priceToCoordinate(price)
      return v == null ? null : (v as number)
    }
    const shapes = p.bins.map((b, i) => {
      const y1 = yOf(b.hi)
      const y2 = yOf(b.lo)
      if (y1 == null || y2 == null) return null
      const top = Math.min(y1, y2)
      const height = Math.max(1, Math.abs(y2 - y1) - 1)
      const len = p.max > 0 ? (b.total / p.max) * barW : 0
      const upLen = b.total > 0 ? len * (b.up / b.total) : 0
      const inVa = b.mid >= p.vaLow && b.mid <= p.vaHigh
      return { i, top, height, len, upLen, inVa, total: b.total, mid: b.mid }
    }).filter(Boolean) as any[]

    const pocY = yOf(p.poc)
    return { shapes, pocY, profile: p, barW }
    // `tick` عمداً در وابستگی‌هاست: با هر جابه‌جایی نمودار، مختصات عوض
    // می‌شود ولی هیچ‌کدام از ورودی‌های دیگر تغییر نمی‌کند.
  }, [chart, series, candles, w, bins, widthRatio, tick])

  if (!view) return <div ref={host} className="pointer-events-none absolute inset-0" />

  return (
    <div ref={host} className="pointer-events-none absolute inset-0">
      <svg width={w} height={h} style={{ overflow: 'hidden' }}>
        {view.shapes.map((s) => (
          <g key={s.i}>
            <rect x={0} y={s.top} width={Math.max(0, s.len)} height={s.height}
              fill={s.inVa ? 'rgba(124,92,255,.34)' : 'rgba(124,92,255,.16)'} />
            <rect x={0} y={s.top} width={Math.max(0, s.upLen)} height={s.height}
              fill="rgba(0,227,154,.30)" />
          </g>
        ))}
        {view.pocY != null && (
          <g>
            <line x1={0} x2={view.barW + 8} y1={view.pocY} y2={view.pocY}
              stroke="#F5C24C" strokeWidth={1.5} />
            <text x={view.barW + 12} y={view.pocY + 3} fontSize="9" fill="#F5C24C">
              POC {compact(view.profile.poc)}
            </text>
          </g>
        )}
      </svg>
    </div>
  )
}

/**
 * `memo`: کارت نمودار به‌خاطر شمارش معکوس هر ثانیه رندر می‌شود؛ بدون این،
 * نیم‌رخ حجم هم هر ثانیه از نو ساخته می‌شد.
 */
export const VolumeProfileLayer = memo(VolumeProfileLayerImpl)
